<template>
  <WidgetCard title="Alert trend by severity" icon="bar-chart" :loading="loading" :error="error">
    <template #headerExtra>
      <a-radio-group :value="chartType" @change="onChangeType">
        <a-radio-button value="bar">
          <a-icon type="bar-chart" />
        </a-radio-button>
        <a-radio-button value="line">
          <a-icon type="line-chart" />
        </a-radio-button>
      </a-radio-group>
    </template>
    <template #toolbar>
      <div class="dw-trend-toolbar">
        <div class="dw-sevtoggles">
          <span class="dw-sevtoggles__label">Filter by severity:</span>
          <span
            v-for="s in severities"
            :key="s.key"
            class="dw-sevtoggle"
            :class="{ 'is-off': !visible[s.key] }"
            @click="toggle(s.key)"
          >
            <span class="dw-sevtoggle__dot" :style="{ background: s.color }" />
            {{ s.label }}
          </span>
        </div>
        <a-radio-group :value="days" @change="onChangeWindow">
          <a-radio-button :value="3">
            3D
          </a-radio-button>
          <a-radio-button :value="7">
            7D
          </a-radio-button>
          <a-radio-button :value="14">
            14D
          </a-radio-button>
          <a-radio-button :value="30">
            30D
          </a-radio-button>
          <a-radio-button :value="90">
            90D
          </a-radio-button>
        </a-radio-group>
      </div>
    </template>
    <div class="dw-chart">
      <BarChart v-if="chartType === 'bar'" :chartData="chartData" :options="chartOptions" :styles="fillStyles" />
      <LineChart v-else :chartData="chartData" :options="chartOptions" :styles="fillStyles" />
    </div>
  </WidgetCard>
</template>

<script>
  import moment from 'moment';
  import WidgetCard from './WidgetCard.vue';
  import BarChart from '@shared/components/BarChart';
  import LineChart from '@shared/components/LineChart';
  import DashboardService from '../../services/dashboard.service';
  import dashboardWidget from '../../mixins/dashboardWidget.mixin';
  import dashboardFilters from '../../mixins/dashboardFilters.mixin';

  const SEVERITIES = [
    { key: 'high', label: 'HIGH', color: '#FF5A00' },
    { key: 'medium', label: 'MED', color: '#f5c518' },
    { key: 'low', label: 'LOW', color: '#1e88e5' }
  ];

  export default {
    name: 'AlertTrendWidget',
    components: { WidgetCard, BarChart, LineChart },
    mixins: [dashboardWidget, dashboardFilters],
    data() {
      return {
        days: 7,
        chartType: 'bar',
        series: [],
        severities: SEVERITIES,
        visible: { high: true, medium: true, low: true },
        // Fill .dw-chart so the canvas is flex-sized (shrinks + grows).
        fillStyles: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }
      };
    },
    computed: {
      chartData() {
        const labels = this.series.map((d) => moment(d.date).format('D MMM'));
        const isLine = this.chartType === 'line';
        const datasets = this.severities
          .filter((s) => this.visible[s.key])
          .map((s) => {
            const data = this.series.map((d) => d[s.key]);
            if (isLine) {
              return {
                label: s.label,
                data,
                borderColor: s.color,
                backgroundColor: s.color,
                pointBackgroundColor: s.color,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                fill: false,
                lineTension: 0
              };
            }
            return {
              label: s.label,
              backgroundColor: s.color,
              data,
              stack: 'severity'
            };
          });
        return { labels, datasets };
      },
      chartOptions() {
        const stacked = this.chartType === 'bar';
        return {
          title: { display: false },
          legend: { display: false },
          scales: {
            xAxes: [
              { stacked, gridLines: { display: false }, categoryPercentage: 0.65, barPercentage: 0.9 }
            ],
            yAxes: [
              { stacked, ticks: { beginAtZero: true, precision: 0 }, gridLines: { color: '#eef0f2' } }
            ]
          },
          tooltips: { mode: 'index', intersect: false },
          responsive: true,
          maintainAspectRatio: false
        };
      }
    },
    methods: {
      cacheDescriptors() {
        return [DashboardService.resources.severityTrend(this.days, this.tagIds, this.planTypes)];
      },
      onChangeWindow(e) {
        this.days = e.target.value;
        this.fetchData();
      },
      onChangeType(e) {
        this.chartType = e.target.value;
      },
      toggle(key) {
        this.visible = { ...this.visible, [key]: !this.visible[key] };
      },
      fetchResource() {
        return DashboardService.fetchSeverityTrend(this.days, this.tagIds, this.planTypes);
      },
      applyResult(res) {
        this.series = res.series;
      }
    }
  };
</script>
